import * as React from 'react';
import {
    AppPlatform,
    ReleaseType,
    AppStoreType,
    ReleaseTypesForNonBlockingSteps,
    ReleaseStepBlockingConfig,
} from '../types';
import Select from 'antd/lib/select';
import { RootState } from '../reducers/reducers';
import {
    selectReleaseStepsByReleaseType,
    selectWorkflowEnabledSteps,
    selectReleaseStepsBlockingConfigForReleaseType,
} from '../selectors/selectors';
import { displayTextForReleaseStepType } from '@/helpers/displayHelpers';
import { useSelector } from 'react-redux';

interface BlockingStepsSelectorProps {
    appId: string;
    platform: AppPlatform;
    appStoreType: AppStoreType;
    releaseType?: ReleaseTypesForNonBlockingSteps;
    value?: string[];
    onChange?: (value: string[]) => void;
}

export const BlockingStepsSelector = ({
    appId,
    platform,
    appStoreType,
    releaseType = ReleaseTypesForNonBlockingSteps.Standard,
    value,
    onChange,
}: BlockingStepsSelectorProps) => {
    const releaseStepsByReleaseType = useSelector((state: RootState) => selectReleaseStepsByReleaseType(state, appId));
    const allReleaseSteps =
        releaseStepsByReleaseType[
            releaseType === ReleaseTypesForNonBlockingSteps.Rollback ? ReleaseType.Rollback : ReleaseType.Minor
        ];
    const workflowEnabledSteps = useSelector((state: RootState) => selectWorkflowEnabledSteps(state, appId));
    const nonBlockingStepsConfigMap = useSelector((state: RootState) =>
        selectReleaseStepsBlockingConfigForReleaseType(state, appId),
    );

    const archetypeOptions = React.useMemo(() => {
        const allBlockingSteps = allReleaseSteps.filter(
            (step): boolean => nonBlockingStepsConfigMap[releaseType][step] === ReleaseStepBlockingConfig.Blocking,
        );
        return Object.values(allBlockingSteps).map((step) => {
            const isStepWorkflowsEnabled = workflowEnabledSteps[step];
            return {
                value: step,
                label: displayTextForReleaseStepType(step, platform, appStoreType, isStepWorkflowsEnabled),
            };
        });
    }, [nonBlockingStepsConfigMap, allReleaseSteps, workflowEnabledSteps, platform, appStoreType]);

    return (
        <Select
            mode="multiple"
            options={archetypeOptions}
            placeholder="Select required steps"
            value={value}
            onChange={onChange}
        />
    );
};
